// Everything about one node, docked inside the tree renderer instead of down the
// left of the page.
//
// This was a sidebar of five stacked cards: statistics, definition, split
// condition, insights, and a loose "Split this node" button under all of it. The
// figures are three numbers and the actions are five links, so a third of the
// page width was carrying about a screen inch of content, and the actions
// themselves read as unrelated because each sat in its own card.
//
// Docking it to the view means it appears where the user is already looking, it
// works the same for the vertical tree, the horizontal tree and the table, and
// the left column is free for the gains chart.
import { action, makeObservable } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import TreeStructure from '../../../models/tree/TreeStructure';
import { PROFILES_PERMISSIONS } from '../../../permissions';
import Store from '../../../store';
import { flattenTree } from '../../../utils';
import checkPermissions from '../CheckPermissions';
import SplitConditionForm from './SplitConditionForm';
import { NodeTraits } from './NodeTraits';
import { baselineRate, formatLift, liftVsBaseline } from './nodeLift';
import { nodeSegment, segmentColor, SEGMENT_LABEL } from './nodeSegment';
import './treeNodes.css';
import './nodeInspector.css';

interface NodeInspectorProps {
  store?: Store;
  treeId: number;
  treeName: string;
  nodeId: number;
  /**
   * A node under the cursor rather than a selected one. Shows the figures and
   * nothing that writes, because a panel of buttons that moves as the cursor
   * moves cannot be clicked.
   */
  preview?: boolean;
  onClose?: () => void;
}

const count = (n?: number) => (n ?? 0).toLocaleString('en-US');
const share = (n?: number) => `${((n ?? 0) * 100).toFixed(1)}%`;

export default inject('store')(
  observer(
    class NodeInspector extends React.Component<
      NodeInspectorProps,
      Record<string, never>
    > {
      constructor(props: NodeInspectorProps) {
        super(props);

        makeObservable(this, {
          toggleInsertChildNode: action,
          toggleReplaceSplitCondition: action,
          toggleDeleteSplitCondition: action,
        });

        this.toggleInsertChildNode = this.toggleInsertChildNode.bind(this);
        this.toggleReplaceSplitCondition =
          this.toggleReplaceSplitCondition.bind(this);
        this.toggleDeleteSplitCondition =
          this.toggleDeleteSplitCondition.bind(this);
        this.deleteSplitCondition = this.deleteSplitCondition.bind(this);
      }

      componentDidMount() {
        // A hover preview must not claim the selection: the tree reads
        // `selected` to draw its ring, and marking it here made every node the
        // cursor crossed look picked.
        if (this.props.preview) return;

        const { treeId, nodeId } = this.props;

        const newTrees = this.props.store.treesPage.trees;
        const newTree = newTrees.find((tree) => tree.id === treeId);
        const stats = newTree.definition.stats.map((node: any) => {
          // eslint-disable-next-line no-param-reassign
          node.selected = node.node === nodeId;
          return node;
        });
        newTree.definition.stats = stats;
        newTrees[treeId - 1] = newTree;
        this.props.store.treesPage.trees = newTrees;
        this.props.store.resetSplitCondition();
      }

      toggleInsertChildNode() {
        this.props.store.analyticsTrackEvent('Added tree split condition');
        this.props.store.treesPage.activeInsertChildNode =
          !this.props.store.treesPage.activeInsertChildNode;
      }

      toggleReplaceSplitCondition() {
        this.props.store.analyticsTrackEvent('Replaced tree split condition');

        this.props.store.treesPage.activeReplaceCondition =
          !this.props.store.treesPage.activeReplaceCondition;
      }

      toggleDeleteSplitCondition() {
        this.props.store.analyticsTrackEvent('Deleted tree split condition');
        this.props.store.treesPage.activeDeleteCondition =
          !this.props.store.treesPage.activeDeleteCondition;
      }

      async deleteSplitCondition() {
        const { treeId, nodeId, treeName } = this.props;
        await this.props.store.deleteSplitCondition(treeId, treeName, nodeId);
      }

      render() {
        const { treesPage, tenant, modelId, isAllowedToEdit, ensemblingPage } =
          this.props.store;
        const {
          trees,
          activeInsertChildNode,
          activeReplaceCondition,
          activeDeleteCondition,
          deletingSplitCondition,
        } = treesPage;
        const { treeId, nodeId, treeName, preview, onClose } = this.props;

        let allNodes: TreeStructure[] = [];
        const tree = trees[treeId - 1];
        if (tree && tree.definition && tree.definition.structure) {
          allNodes = flattenTree(tree.definition.structure);
        }

        const node: TreeStructure = allNodes.length && allNodes[nodeId - 1];
        if (!node) {
          return (
            <div className="tins">
              <Alert variant="info" className="m-0">
                Select a node first
              </Alert>
            </div>
          );
        }

        const rate = node.population ? node.conversions / node.population : 0;
        const thresholds = ensemblingPage?.ensemble?.thresholds;
        const segment = nodeSegment(rate, thresholds);

        // Lift is the reason to care about a node at all, so it leads and the
        // counts that qualify it follow. Baseline is the root node, the same
        // denominator the AI model summary uses.
        const baseline = baselineRate(allNodes[0]);
        const lift = liftVsBaseline(rate, baseline);

        const nodePath = `/tenant/${tenant}/models/${modelId}/model/trees/${treeId}/nodes/${nodeId}`;

        const confirmDeleteDialog = (
          <Modal
            onHide={this.toggleDeleteSplitCondition}
            show={activeDeleteCondition}
          >
            <Modal.Header>
              <Modal.Title>
                Are you sure you want to delete node {nodeId}&apos;s split
                condition?
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                There&apos;s no <code>undo</code> button
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button
                onClick={this.toggleDeleteSplitCondition}
                disabled={deletingSplitCondition}
                variant="outline-primary"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={this.deleteSplitCondition}
                disabled={deletingSplitCondition}
              >
                {deletingSplitCondition
                  ? 'Deleting condition...'
                  : 'Delete split condition'}
              </Button>
            </Modal.Footer>
          </Modal>
        );

        return (
          <>
            {!preview && (
              <>
                <SplitConditionForm
                  treeId={treeId}
                  treeName={treeName}
                  nodeId={nodeId}
                  active={activeInsertChildNode}
                />
                <SplitConditionForm
                  treeId={treeId}
                  treeName={treeName}
                  nodeId={nodeId}
                  active={activeReplaceCondition}
                  replace
                />
                {confirmDeleteDialog}
              </>
            )}
            <div className={`tins ${preview ? 'tins--preview' : ''}`}>
              <div className="tins-head">
                <span className="tins-title">Node {nodeId}</span>
                {segment && (
                  <span
                    className="tins-badge"
                    style={{ background: segmentColor(segment) }}
                  >
                    {SEGMENT_LABEL[segment]}
                  </span>
                )}
                {!preview && onClose && (
                  <button
                    type="button"
                    className="tins-close"
                    onClick={onClose}
                    aria-label="Close node details"
                  >
                    &times;
                  </button>
                )}
              </div>

              {lift !== null && (
                <div className="tins-lead">
                  <span
                    className={`tins-lead-value ${
                      lift < 1 ? 'tins-lead-value--under' : ''
                    }`}
                  >
                    {formatLift(lift)}
                  </span>
                  {/* One line, so the figure and its qualifier share a
                      baseline. The long form wrapped to two lines and the
                      figure then read as floating beside a paragraph. */}
                  <span className="tins-lead-label">
                    vs {(baseline * 100).toFixed(1)}% dataset average
                  </span>
                </div>
              )}

              {/* Count and share are one fact, so they read as one value, and
                  the three sit on one row. As boxed cells over two rows they
                  took a third of the panel to carry three numbers. "Leads"
                  rather than "population", which is what the rest of the page
                  calls them. */}
              <dl className="tins-stats">
                <div>
                  <dt>Leads</dt>
                  <dd>
                    {count(node.population)}{' '}
                    <span>({share(node.percentPopulation)})</span>
                  </dd>
                </div>
                <div>
                  <dt>Conversions</dt>
                  <dd>
                    {count(node.conversions)}{' '}
                    <span>({share(node.percentConversions)})</span>
                  </dd>
                </div>
                <div>
                  <dt>Rate</dt>
                  <dd>{(rate * 100).toFixed(2)}%</dd>
                </div>
              </dl>

              {/* Both sections used to be a bare noun, "Splits on" and
                  "Profile", which left it to the reader to work out that one
                  described everything true on the way down to this node and
                  the other described the test that creates its children. They
                  now say so, and they run in that order. */}
              {node.sumConditions && (
                <div className="tins-section">
                  <span className="tins-label">Leads in this node</span>
                  <div title={node.sumConditions}>
                    <NodeTraits condition={node.sumConditions} max={6} />
                  </div>
                </div>
              )}

              {node.splitConditions && (
                <div className="tins-section">
                  <span className="tins-label">Split into children by</span>
                  {/* Was split on the literal text "AND", which breaks on an
                      AND inside a string value. Parsed now. The SQL stays
                      reachable on hover for the people who edit conditions. */}
                  <div title={node.splitConditions}>
                    <NodeTraits condition={node.splitConditions} max={6} />
                  </div>
                </div>
              )}

              {preview ? (
                <p className="tins-hint">Click the node to edit it</p>
              ) : (
                /* Six actions of equal visual weight in three unlabelled rows
                   read as a pile of buttons rather than a set of choices.
                   There is one thing you come here to do, so it is the full
                   width primary, and the rest sit under a heading that says
                   what they are for. Same headings as the sections above, so
                   they read as part of the panel. */
                <div className="tins-actions">
                  <button
                    type="button"
                    className="tins-btn tins-btn--primary"
                    onClick={this.toggleInsertChildNode}
                    disabled={!nodeId || !isAllowedToEdit}
                  >
                    Split this node
                  </button>

                  <div className="tins-group">
                    <span className="tins-label">Explore</span>
                    <div className="tins-btn-row">
                      <Link
                        className="tins-btn"
                        title="View a sample of converters in this node"
                        to={`${nodePath}/leads/1?includePersonal=false`}
                      >
                        Converters
                      </Link>
                      <Link
                        className="tins-btn"
                        title="View a sample of non converters in this node"
                        to={`${nodePath}/leads/0?includePersonal=false`}
                      >
                        Non converters
                      </Link>
                    </div>
                  </div>

                  {node.splitConditions &&
                    checkPermissions(
                      PROFILES_PERMISSIONS.ARCHITECT,
                      <div className="tins-group">
                        {/* The heading is what makes these two unambiguous.
                            On their own, "Delete" beside a node panel reads
                            as deleting the node. */}
                        <span className="tins-label">
                          Edit the split condition
                        </span>
                        <div className="tins-btn-row">
                          <button
                            type="button"
                            className="tins-btn"
                            onClick={this.toggleReplaceSplitCondition}
                            disabled={!nodeId || !isAllowedToEdit}
                          >
                            Replace
                          </button>
                          <button
                            type="button"
                            className="tins-btn tins-btn--danger"
                            onClick={this.toggleDeleteSplitCondition}
                            disabled={!isAllowedToEdit}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>
          </>
        );
      }
    }
  )
);
